import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import api from '../../services/api';
import ServerDataTable from '../ServerDataTable/ServerDataTable';
import { formatDate } from '../../utils/formatDate';
import StatusBadge from '../StatusBadge';

export default function SyncedPricingTab() {
  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    last_action: '',
    price_from: '',
    price_to: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    last_action: '',
    price_from: '',
    price_to: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  // Function to update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Clear existing filter parameters
    Object.keys(filters).forEach(key => {
      params.delete(key);
    });

    // Add non-empty filter values to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());
    params.set('search', currentSearchTerm || '');
    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters
    const filtersFromURL = {
      last_action: params.get('last_action') || '',
      price_from: params.get('price_from') || '',
      price_to: params.get('price_to') || '',
      updated_at_start: params.get('updated_at_start') || '',
      updated_at_end: params.get('updated_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Sync modal filters with applied filters when dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch pricing records with pagination and filters
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['pricingRecords', pageIndex, pageSize, debouncedSearchTerm, appliedFilters],
    staleTime: 0,
    queryFn: async () => {
      const params = {
        offset: pageIndex * pageSize,
        limit: pageSize,
        search: debouncedSearchTerm || undefined,
        last_action: appliedFilters.last_action || undefined,
        price_from: appliedFilters.price_from || undefined,
        price_to: appliedFilters.price_to || undefined,
        updated_at_start: appliedFilters.updated_at_start || undefined,
        updated_at_end: appliedFilters.updated_at_end || undefined
      };

      const response = await api.getSyncedPricing(params);

      // Transform the response data
      const transformedData = {
        items: response.data.data.map((item) => ({
          id: item.id,
          shopifyRuleId: item.shopify_rule_id,
          shopifyRuleName: item.shopify_rule_name,
          erpCustomerId: item.erp_customer_id,
          erpItemCode: item.erp_item_code,
          erpItemSku:
            item?.shopify_sku.split('-')[
              item?.shopify_sku.split('-').length - 1
            ] || '-',
          shopifySku: item.shopify_sku,
          shopifyVariantId: item.shopify_variant_id,
          price: parseFloat(item.price),
          lastAction: item?.last_action?.toUpperCase() || '-',
          createdAt: item.created_at,
          updatedAt: item.updated_at
        })),
        pagination: {
          total: response.data.totalItems || 0,
          limit: response.data.limit || pageSize,
          offset: response.data.offset || 0,
          hasMore: response.data.hasMore || false
        }
      };

      // If we're not on the first pageIndex and the total results would make current pageIndex empty
      const totalPages = Math.ceil(
        transformedData.pagination.total / pageSize
      );
      if (pageIndex > 0 && pageIndex >= totalPages) {
        setPageIndex(0);
        return refetch();
      }

      return transformedData;
    }
  });

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const columns = [
    {
      field: 'erpCustomerId',
      headerName: 'ERP Customer',
      width: 150,
      sortable: true
    },
    {
      field: 'erpItemCode',
      headerName: 'ERP Item',
      width: 150,
      sortable: true
    },
    {
      field: 'erpItemSku',
      headerName: 'ERP Item Variant',
      width: 150,
      sortable: true
    },
    {
      field: 'shopifyRuleName',
      headerName: 'Rule Name',
      width: 200,
      sortable: true
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 150,
      sortable: true,
      align: 'left',
      renderCell: ({ value }) => {
        return <span>${value}</span>;
      }
    },
    {
      field: 'lastAction',
      headerName: 'Last Action',
      width: 150,
      sortable: true,
      renderCell: ({ value }) => {
        return <StatusBadge status={value?.toUpperCase() || 'N/A'} />;
      }
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton size='small' onClick={() => handleOpenViewDialog(row)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, newSearchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  const handleOpenViewDialog = (data) => {
    setViewData(data);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Clear filters and close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      last_action: '',
      price_from: '',
      price_to: '',
      updated_at_start: '',
      updated_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  return (
    <Box>
      <ServerDataTable
        columns={columns}
        currentPage={pageIndex}
        data={data?.items || []}
        totalRows={data?.pagination?.total || 0}
        sortConfig={sortConfig}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        noDataMessage='No pricing records found'
        rowsPerPage={pageSize}
        loading={isLoading || isFetching}
        isError={error}
        error={error}
        totalPages={Math.ceil(
          (data?.pagination?.total || 0) / pageSize
        )}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by ERP Customer ID, ERP Item Code, Shopify SKU'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some((value) => value !== '') && (
                <Chip
                  size='small'
                  label={isFetching ? '' : Object.values(appliedFilters).filter((value) => value !== '').length}
                  color='secondary'
                  sx={{ ml: 1 }}
                  disabled={isFetching}
                />
              )}
            </Button>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isFetching}>
              Refresh
            </Button>
          </Box>
        }
      />

      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Pricing Rules
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Last Action</InputLabel>
              <Select
                name='last_action'
                value={filterForm.last_action}
                onChange={handleFilterFormChange}
                label='Last Action'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='create'>Create</MenuItem>
                <MenuItem value='update'>Update</MenuItem>
              </Select>
            </FormControl>
            <Divider>Price Range</Divider>
            <Box display='flex' gap={2}>
              <TextField
                name='price_from'
                label='From Price'
                type='number'
                value={filterForm.price_from}
                onChange={handleFilterFormChange}
                fullWidth
                inputProps={{ min: 0, step: 'any' }}
              />
              <TextField
                name='price_to'
                label='To Price'
                type='number'
                value={filterForm.price_to}
                onChange={handleFilterFormChange}
                fullWidth
                inputProps={{ min: 0, step: 'any' }}
              />
            </Box>
            <Divider>Updated At Range</Divider>
            <TextField
              name='updated_at_start'
              label='From Date'
              type='date'
              value={filterForm.updated_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name='updated_at_end'
              label='To Date'
              type='date'
              value={filterForm.updated_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}
        >
          Pricing Rule Details
          <Box>
            <Tooltip
              title={isDialogMaximized ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
                {isDialogMaximized ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={3}>
              {/* Rule Information Section */}
              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Rule Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Rule Name
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontWeight: 500 }}
                >
                  {viewData.shopifyRuleName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Shopify Rule ID
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                >
                  {viewData.shopifyRuleId || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Price
                </Typography>
                <Typography variant='h6' color='success.main' gutterBottom>
                  {formatPrice(viewData.price)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Last Action
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusBadge status={viewData.lastAction?.toUpperCase() || 'N/A'} />
                </Box>
              </Grid>

              {/* ERP Information Section */}
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  color='primary'
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  ERP Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  ERP Customer ID
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontFamily: 'monospace' }}
                >
                  {viewData.erpCustomerId}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  ERP Item Code
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontFamily: 'monospace' }}
                >
                  {viewData.erpItemCode}
                </Typography>
              </Grid>

              {/* Shopify Information Section */}
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  color='primary'
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Shopify Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Shopify SKU
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontFamily: 'monospace' }}
                >
                  {viewData.shopifySku}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Shopify Variant ID
                </Typography>
                <Typography
                  variant='body1'
                  gutterBottom
                  sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                >
                  {viewData.shopifyVariantId}
                </Typography>
              </Grid>

              {/* Timestamps Section */}
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  color='primary'
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Timestamps
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Created
                </Typography>
                <Typography variant='body1' gutterBottom>
                  {formatDate(viewData.createdAt)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Last Updated
                </Typography>
                <Typography variant='body1' gutterBottom>
                  {formatDate(viewData.updatedAt)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
